<template>
  <div data-component-group="contract-form" data-component="prices">
    <slot name="beforeForm" />

    <form novalidate v-if="!isDisabled" @submit.prevent="addPrice">
      <magic-container class="single-line">
        <v-col cols="12" class="padding-right">
          <base-date-picker
            :date="from"
            :config="dateConfig"
            :is-required="true"
            :is-disabled="isDisabled"
            placeholder="From date"
            :on-blur="v$.from.$touch"
            :error-messages="fromErrors"
            :on-change="onChangeFrom"
          />
        </v-col>

        <v-col>
          <v-text-field
            v-model="price"
            label="Price"
            required
            type="number"
            :prefix="currencySymbol"
            :readonly="isDisabled"
            :autofocus="true"
            :error-messages="priceErrors"
            @blur="v$.price.$touch()"
          />
        </v-col>
      </magic-container>

      <div v-if="!isDisabled" class="form-actions">
        <v-btn type="submit" color="secondary" class="trigger" :loading="isLoading">
          <v-icon>{{ triggerIcon }}</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </form>

    <div v-if="!isDisabled">
      <div class="info-box">
        {{ $t('contract_prices_info') }}
      </div>
    </div>

    <Removables
      v-if="hasContract"
      :items="prices"
      :on-remove="removePrice"
      :is-disabled="isDisabled"
    >
      <template #item="scope">
        <AmountLabel
          label="Price"
          size="lg"
          :value="getFormattedPrice(scope.item.price)"
          :style="hasActiveStyle(scope.item.from)"
        />
        <AmountLabel
          label="From date"
          :value="scope.item.from"
          :style="hasActiveStyle(scope.item.from)"
        />
      </template>
    </Removables>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import AmountLabel from '../../labels/Amount';
  import Removables from '../../lists/Removables';
  import { isObj, isStr } from '../../../utils';
  // TODO: Translate component

  export default {
    components: {
      AmountLabel,
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        price: {
          required,
        },
        from: {
          required,
        },
      };
    },
    props: {
      contract: {
        type: Object,
        required: true,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        isLoading: false,
        price: '',
        from: '',
        dateConfig: {
          minDate: 'today',
        },
      };
    },
    computed: {
      activeContract() {
        return this.$store.getters['contracts/activeItem'];
      },
      prices() {
        return this.activeContract.prices;
      },
      hasContract() {
        return isObj(this.contract);
      },
      currencySymbol() {
        const { contract } = this;
        return isObj(contract) && isObj(contract.country.currency)
          ? contract.country.currency.symbol
          : '';
      },
      priceErrors() {
        return this.v$.price.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Price is required';
          }
          return error.$message;
        });
      },
      fromErrors() {
        return this.v$.from.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'From date is required';
          }
          return error.$message;
        });
      },
      triggerIcon() {
        const isFromAlreadySet = this.isFromDateAlreadySet();

        return isFromAlreadySet ? 'update' : 'add';
      },
      triggerLabel() {
        const isFromAlreadySet = this.isFromDateAlreadySet();

        return isFromAlreadySet ? this.$t('update') : this.$t('add');
      },
    },
    watch: {
      contract(newContract, oldContract) {
        if (newContract.id !== oldContract.id) {
          this.clear();
        }
      },
      isDisabled(newValue) {
        if (newValue) {
          this.clear();
        }
      },
    },
    methods: {
      onChangeFrom(from) {
        this.from = from;

        if (this.prices.length) {
          const selectedItem = this.prices.filter((p) => p.from === from);

          if (isObj(selectedItem[0])) {
            this.price = selectedItem[0].price;
            return;
          }

          this.price = '';
        }
      },
      removePrice(item) {
        const query = {
          id: this.contract.id,
          price_id: item.id,
        };

        return this.$store.dispatch('contracts/removePrice', query);
      },
      getFormattedPrice(price) {
        const { currency } = this.contract.country;

        return isObj(currency) && isStr(currency.symbol)
          ? `${price} ${currency.symbol}`
          : price;
      },
      addPrice() {
        if (this.isLoading) return;

        this.v$.$touch();
        if (this.v$.$invalid) return;

        const { contract, price, from } = this;
        const query = {
          id: contract.id,
          price: Number(price),
          from,
        };

        this.isLoading = true;

        return this.$store.dispatch('contracts/addPrice', query)
          .catch(err => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      isFromDateAlreadySet() {
        const { prices, from } = this;
        let result = false;

        if (prices.length) {
          const pricesFrom = prices.map((p) => p.from);

          if (pricesFrom.includes(from)) {
            result = true;
          }
        }

        return result;
      },
      hasActiveStyle(item) {
        return item === this.from ? 'color: #FB8C00;' : '';
      },
      clear() {
        this.price = '';
        this.from = '';
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='contract-form'][data-component='prices'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .info-box {
    margin-top: 20px;
    padding: 8px 16px;
    font-size: 12px;
    color: #424242;
    background-color: #1976d212;
    border-radius: 2px;

    .v-theme--dark & {
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .single-line > .padding-right {
    padding-right: 20px;
  }

  .fluid-layout {
    flex-direction: column;

    &.single-line {
      flex-direction: row;
    }
  }

  .v-list {
    background: transparent;
  }

  .price-item {
    margin: 0 0 10px 0;
    border-radius: $border-radius;
    background-color: rgb(var(--v-theme-surface));
  }

// TODO replace with form-buttons
  .form-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  [data-component='amount'] {
    .item-label {
      min-width: 80px;
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
